/**
 * Platform Users — dedicated platform-wide user management.
 * Server-side search, filters, pagination. Shows all memberships.
 * Drawer includes: profile, memberships with role editing, identity editing, suspend actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  PencilIcon,
  UserMinusIcon,
  UserPlusIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { usePlatformUsers } from '../features/super-admin/hooks/usePlatformUsers';
import {
  getUserDetail,
  changeUserTenantRole,
  suspendUser,
  updateUserIdentity,
  deleteUser,
} from '../features/super-admin/api/superAdminApi';
import { LoadingState, ErrorState, EmptyState, StatusBadge } from '../features/super-admin/components/shared';
import type { UserDetail, UserMembership } from '../features/super-admin/types';
import { TENANT_ROLE_OPTIONS } from '../features/super-admin/types';

const PlatformUsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUserId = searchParams.get('user') ? Number(searchParams.get('user')) : null;

  const {
    users, total, page, loading, error,
    search, roleFilter, statusFilter,
    setSearch, setRoleFilter, setStatusFilter, setPage, refresh,
  } = usePlatformUsers();

  const [drawerUser, setDrawerUser] = useState<UserDetail | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);

  // Load user detail when selected
  useEffect(() => {
    if (!selectedUserId) { setDrawerUser(null); return; }
    let cancelled = false;
    setDrawerLoading(true);
    setDrawerError(null);
    getUserDetail(selectedUserId)
      .then((data) => { if (!cancelled) setDrawerUser(data.user); })
      .catch((err) => { if (!cancelled) setDrawerError(err.message || 'Failed to load user'); })
      .finally(() => { if (!cancelled) setDrawerLoading(false); });
    return () => { cancelled = true; };
  }, [selectedUserId]);

  const closeDrawer = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const refreshDrawer = useCallback(() => {
    if (!selectedUserId) return;
    setDrawerLoading(true);
    getUserDetail(selectedUserId)
      .then((data) => setDrawerUser(data.user))
      .catch(() => {})
      .finally(() => setDrawerLoading(false));
  }, [selectedUserId]);

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-4">
      {/* Page heading */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Platform Users</h1>
          <p className="text-sm text-slate-500">{total.toLocaleString()} users across all organisations</p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-white transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, or organisation..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        >
          <option value="">All roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="project_manager">Project Manager</option>
          <option value="agronomist">Agronomist</option>
          <option value="field_facilitator">Field Facilitator</option>
          <option value="farmer">Farmer</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Table */}
      {error && <ErrorState message={error} onRetry={refresh} compact />}
      {loading && <LoadingState rows={8} />}

      {!loading && users.length === 0 && (
        <EmptyState title="No users found" subtitle="Try adjusting your search or filters." />
      )}

      {!loading && users.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Name</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Email</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Role</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Organisations</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Status</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Last Login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setSearchParams({ user: String(u.id) }, { replace: true })}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                          {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <span className="font-medium text-slate-800 truncate">{u.full_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 truncate max-w-[200px]">{u.email}</td>
                    <td className="px-4 py-2.5"><StatusBadge value={u.global_role || u.role} type="role" /></td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {u.memberships?.slice(0, 2).map((m: UserMembership) => (
                          <span key={m.tenant_id} className="text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                            {m.tenant_name}
                          </span>
                        ))}
                        {(u.memberships?.length ?? 0) > 2 && (
                          <span className="text-xs text-slate-400">+{(u.memberships?.length ?? 0) - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge value={u.is_active ? 'active' : 'inactive'} type="user" />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">
                      {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 text-xs text-slate-500">
              <span>Page {page} of {totalPages} ({total.toLocaleString()} total)</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* User detail drawer */}
      {selectedUserId && (
        <UserDrawer
          user={drawerUser}
          loading={drawerLoading}
          error={drawerError}
          onClose={closeDrawer}
          onRefresh={refreshDrawer}
        />
      )}
    </div>
  );
};

// ─── User Drawer ─────────────────────────────────────────────────────────────

function UserDrawer({ user, loading, error, onClose, onRefresh }: {
  user: UserDetail | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'profile' | 'memberships' | 'identity'>('profile');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const clearMsg = () => setTimeout(() => setActionMsg(null), 3000);

  const handleSuspend = async () => {
    if (!user) return;
    const action = user.is_active ? 'suspend' : 'reactivate';
    if (!window.confirm(`${action === 'suspend' ? 'Suspend' : 'Reactivate'} ${user.full_name}?`)) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      const result = await suspendUser(user.id, user.is_active);
      setActionMsg({ type: 'success', text: result.message });
      onRefresh();
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
      clearMsg();
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    if (!window.confirm(`Permanently delete ${user.full_name}? This cannot be undone.`)) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      await deleteUser(user.id);
      setActionMsg({ type: 'success', text: 'User deleted' });
      setTimeout(onClose, 1000);
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
      clearMsg();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-800 truncate">{user?.full_name || 'User Details'}</h2>
            {user && <p className="text-xs text-slate-500 truncate">{user.email}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 shrink-0 ml-2"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Action feedback */}
        {actionMsg && (
          <div className={`px-4 py-2 text-xs font-medium ${actionMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {actionMsg.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200 shrink-0">
          {(['profile', 'memberships', 'identity'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && <LoadingState message="Loading user..." />}
          {error && <ErrorState message={error} compact />}
          {!loading && !error && !user && <EmptyState title="User not found" />}

          {/* Profile tab */}
          {!loading && !error && user && activeTab === 'profile' && (
            <ProfileTab user={user} />
          )}

          {/* Memberships tab */}
          {!loading && !error && user && activeTab === 'memberships' && (
            <MembershipsTab
              user={user}
              actionLoading={actionLoading}
              onRoleChanged={onRefresh}
            />
          )}

          {/* Identity tab */}
          {!loading && !error && user && activeTab === 'identity' && (
            <IdentityTab user={user} onSaved={onRefresh} />
          )}
        </div>

        {/* Footer actions */}
        {user && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 shrink-0 bg-slate-50">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSuspend}
                disabled={actionLoading || user.role === 'super_admin'}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50 transition-colors"
                title={user.role === 'super_admin' ? 'Cannot suspend super admins' : undefined}
              >
                {user.is_active ? (
                  <><UserMinusIcon className="w-3.5 h-3.5" /> Suspend</>
                ) : (
                  <><UserPlusIcon className="w-3.5 h-3.5" /> Reactivate</>
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={handleDelete}
              disabled={actionLoading || user.role === 'super_admin'}
              className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Profile Tab ─────────────────────────────────────────────────────────────

function ProfileTab({ user }: { user: UserDetail }) {
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center text-lg font-bold text-emerald-700 shrink-0">
          {user.full_name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="min-w-0">
          <p className="font-bold text-slate-800 truncate">{user.full_name}</p>
          <p className="text-sm text-slate-500 truncate">{user.email}</p>
          {user.phone && <p className="text-xs text-slate-400">{user.phone}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <InfoCell label="Global Role">
          <StatusBadge value={user.global_role || user.role} type="role" />
        </InfoCell>
        <InfoCell label="Status">
          <StatusBadge value={user.is_active ? 'active' : 'inactive'} type="user" />
        </InfoCell>
        <InfoCell label="Phone">{user.phone || '—'}</InfoCell>
        <InfoCell label="Created">
          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
        </InfoCell>
        <InfoCell label="Last Login">
          {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
        </InfoCell>
        <InfoCell label="Workspaces">
          {user.workspace_count ?? user.memberships?.length ?? 0}
        </InfoCell>
      </div>

      {user.farmer_profiles && user.farmer_profiles.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Farmer Profiles</h4>
          <div className="space-y-1">
            {user.farmer_profiles.map((fp) => (
              <div key={fp.id} className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <span className="text-slate-700">{fp.full_name || fp.farmer_code || `#${fp.id}`}</span>
                <span className="text-xs text-slate-400">{fp.tenant_name || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Memberships Tab ─────────────────────────────────────────────────────────

function MembershipsTab({ user, actionLoading, onRoleChanged }: {
  user: UserDetail;
  actionLoading: boolean;
  onRoleChanged: () => void;
}) {
  const [editingTenant, setEditingTenant] = useState<number | null>(null);
  const [newRole, setNewRole] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleMsg, setRoleMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const startRoleEdit = (m: UserMembership) => {
    setEditingTenant(m.tenant_id);
    setNewRole(m.role);
    setRoleMsg(null);
  };

  const saveRole = async (m: UserMembership) => {
    setRoleSaving(true);
    setRoleMsg(null);
    try {
      await changeUserTenantRole(user.id, m.tenant_id, newRole);
      setRoleMsg({ type: 'success', text: `Role updated to ${newRole.replace(/_/g, ' ')}` });
      setEditingTenant(null);
      onRoleChanged();
    } catch (err: unknown) {
      setRoleMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setRoleSaving(false);
      setTimeout(() => setRoleMsg(null), 3000);
    }
  };

  return (
    <div className="p-4 space-y-3">
      {roleMsg && (
        <div className={`px-3 py-2 rounded-lg text-xs font-medium ${roleMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {roleMsg.text}
        </div>
      )}

      {(!user.memberships || user.memberships.length === 0) ? (
        <EmptyState title="No memberships" subtitle="This user has no organisation memberships." />
      ) : (
        user.memberships.map((m: UserMembership) => (
          <div key={m.tenant_id} className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{m.tenant_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <StatusBadge value={m.status} type="membership" />
                  {m.last_active_at && (
                    <span className="text-xs text-slate-400">
                      Last active: {new Date(m.last_active_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              {editingTenant !== m.tenant_id && (
                <button
                  type="button"
                  onClick={() => startRoleEdit(m)}
                  disabled={user.role === 'super_admin'}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  <PencilIcon className="w-3 h-3" />
                  Edit Role
                </button>
              )}
            </div>

            {/* Inline role editor */}
            {editingTenant === m.tenant_id ? (
              <div className="mt-2 flex items-center gap-2">
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                >
                  {TENANT_ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => saveRole(m)}
                  disabled={roleSaving || newRole === m.role}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {roleSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingTenant(null)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="mt-2">
                <StatusBadge value={m.role} type="role" />
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ─── Identity Tab ────────────────────────────────────────────────────────────

function IdentityTab({ user, onSaved }: { user: UserDetail; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    email: user.email || '',
    phone: user.phone || '',
    full_name: user.full_name || '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const body: Record<string, string> = {};
      if (form.email !== user.email) body.email = form.email;
      if (form.phone !== (user.phone || '')) body.phone = form.phone;
      if (form.full_name !== user.full_name) body.full_name = form.full_name;
      if (Object.keys(body).length === 0) { setEditing(false); return; }

      const result = await updateUserIdentity(user.id, body);
      if (result.warning) {
        setMsg({ type: 'error', text: `Warning: ${result.warning}` });
      } else {
        setMsg({ type: 'success', text: 'Identity updated' });
      }
      setEditing(false);
      onSaved();
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <p className="text-xs font-medium text-amber-700">
          Identity changes (email, phone, name) affect login across all organisations.
        </p>
      </div>

      {msg && (
        <div className={`px-3 py-2 rounded-lg text-xs font-medium ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      {editing ? (
        <div className="space-y-3">
          <Field label="Full Name">
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </Field>
          <Field label="Email (Login ID)">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </Field>
          <Field label="Phone">
            <input
              type="text"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              placeholder="Optional"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </Field>
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setForm({ email: user.email, phone: user.phone || '', full_name: user.full_name }); }}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <InfoCell label="Email">{user.email}</InfoCell>
            <InfoCell label="Phone">{user.phone || '—'}</InfoCell>
            <InfoCell label="Full Name">{user.full_name}</InfoCell>
            <InfoCell label="Account Status">
              <StatusBadge value={user.is_active ? 'active' : 'inactive'} type="user" />
            </InfoCell>
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
            Edit Identity
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Utility Components ──────────────────────────────────────────────────────

function InfoCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg px-3 py-2">
      <p className="text-xs text-slate-500">{label}</p>
      <div className="mt-0.5 text-sm font-medium text-slate-700">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default PlatformUsersPage;
